<template>
  <div class="stockhigh">
    <!-- LOADING -->
    <transition name="fade">
      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
      </div>
    </transition>

    <!-- HEADER -->
    <header class="header">
      <div class="container-max">
        <div class="hero">
          <div class="mark">ST</div>
          <div>
            <h1 class="title">Relatório — Stock Alto</h1>
            <p class="subtitle">produtos com maior quantidade em armazém</p>
          </div>

          <div class="spacer"></div>

          <!-- STOCK MIN FILTER -->
          <div class="stock-filter mr-3">
            <label for="minStock" class="mr-2">Stock mínimo:</label>
            <b-form-input
              id="minStock"
              type="number"
              v-model.number="stockfiler"
              min="0"
              size="sm"
              @change="fetchAll"
            />
            <div class="quick-buttons">
              <b-button size="sm" variant="light" @click="setMinStock(20)"
                >≥ 20</b-button
              >
              <b-button size="sm" variant="light" @click="setMinStock(50)"
                >≥ 50</b-button
              >
              <b-button size="sm" variant="light" @click="setMinStock(100)"
                >≥ 100</b-button
              >
              <b-button size="sm" variant="light" @click="setMinStock(200)"
                >≥ 200</b-button
              >
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="setMinStock(0)"
                >Todos</b-button
              >
            </div>
          </div>

          <!-- AÇÕES -->
          <div class="actions">
            <b-button
              size="sm"
              variant="success"
              class="ml-2"
              @click="exportCsv"
            >
              ⬇️ Exportar CSV
            </b-button>
          </div>
        </div>
      </div>
    </header>

    <!-- INFO-BAR (compacta) -->
    <section class="container-max">
      <div class="infobar">
        <div class="infochip">
          <div class="l">Produtos (após filtros)</div>
          <div class="v">{{ sorted.length }}</div>
        </div>
        <div class="infochip">
          <div class="l">Stock total</div>
          <div class="v">{{ totalStock }}</div>
        </div>
        <div class="infochip">
          <div class="l">Média por produto</div>
          <div class="v">{{ avgStock }}</div>
        </div>
        <div class="spacer"></div>
        <small class="muted"
          >Ordenado por: <strong>{{ sortKey }}</strong> ({{ sortDir }})</small
        >
      </div>
    </section>

    <!-- TABELA (LARGURA MÁXIMA) -->
    <section class="container-max">
      <div class="card shadow">
        <div class="table-wrap">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th @click="toggleSort('id_backoffice')" class="th-sort w-id">
                  ID
                  <span
                    class="caret"
                    :class="caretClass('id_backoffice')"
                  ></span>
                </th>
                <th class="w-img">Imagem</th>
                <th class="w-sku">SKU / CNP</th>
                <th>Nome</th>
                <th class="w-ean">EAN</th>
                <th @click="toggleSort('stock')" class="th-sort w-stock">
                  Stock <span class="caret" :class="caretClass('stock')"></span>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="p in sorted"
                :key="p.id_backoffice"
                class="row-click"
                @click="openProduct(p.id_backoffice, $event)"
                :title="`Abrir /product/${p.id_backoffice}`"
              >
                <td class="text-mono">{{ p.id_backoffice }}</td>

                <!-- thumbnail -->
                <td class="thumb" @click.stop>
                  <img :src="p.image || placeholder" alt="" />
                </td>

                <!-- SKUs como chips -->
                <td class="sku-chips" @click.stop>
                  <span
                    v-for="code in p.sku || []"
                    :key="code"
                    class="sku-chip"
                    :title="code"
                  >
                    {{ code }}
                  </span>
                </td>

                <td class="name">
                  <div class="name-text" :title="p.name">{{ p.name }}</div>
                </td>

                <td class="ean">
                  <span
                    v-for="code in p.ean || []"
                    :key="code"
                    class="ean-chip"
                    :title="code"
                  >
                    {{ code }}
                  </span>
                </td>

                <td>
                  <span :class="['badge', stockBadge(p)]">{{ p.stock }}</span>
                </td>
              </tr>

              <tr v-if="!sorted.length && !loading">
                <td colspan="7" class="py-5">
                  <div class="empty">
                    <div class="ico">📦</div>
                    <div class="t1">Sem resultados</div>
                    <div class="t2">Ajusta a pesquisa.</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="card-footer d-flex justify-content-between align-items-center"
        >
          <small class="text-muted"
            >A mostrar {{ sorted.length }} produtos</small
          >
          <small class="text-muted"
            >Ordenado por: <strong>{{ sortKey }}</strong> ({{ sortDir }})</small
          >
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

function debounce(fn, wait = 250) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

export default {
  name: 'StockReportHigh',
  data() {
    return {
      stockfiler: 95,
      loading: false,
      error: null,
      items: [],
      searchInput: '',
      searchTerm: '',
      sortKey: 'stock',
      sortDir: 'desc',
      placeholder:
        'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60"><rect width="100%" height="100%" fill="%23eef2f7"/><text x="50%" y="52%" font-size="12" text-anchor="middle" fill="%236b7a8c" font-family="sans-serif">—</text></svg>',
    };
  },

  computed: {
    totalStock() {
      return this.sorted.reduce((sum, p) => sum + Number(p.stock || 0), 0);
    },
    avgStock() {
      if (!this.sorted.length) return 0;
      return Math.round(this.totalStock / this.sorted.length);
    },
    filtered() {
      const q = (this.searchTerm || '').toLowerCase().trim();
      if (!q) return this.items;
      return this.items.filter((p) => {
        const pool = [
          String(p.id_backoffice || ''),
          p.name || '',
          ...(p.sku || []),
          ...(p.ean || []),
        ]
          .join(' ')
          .toLowerCase();
        return pool.includes(q);
      });
    },
    sorted() {
      const arr = [...this.filtered];
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const key = this.sortKey;
      arr.sort((a, b) => {
        const va = this.sortVal(a, key);
        const vb = this.sortVal(b, key);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
      return arr;
    },
  },

  async created() {
    await this.fetchAll();
    this.debouncedApplySearch = debounce(() => {
      this.searchTerm = this.searchInput;
    }, 250);
  },

  methods: {
    async fetchAll() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.post(
          `${this.$store.state.apiUrl}products/stockReport`,
          { minStock: this.stockfiler }
        );
        this.items = (data?.data || data || []).map(this.normalizeItem);
      } catch (e) {
        this.error = e;
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      await this.fetchAll();
    },
    setMinStock(n) {
      this.stockfiler = Number(n) || 0;
      this.fetchAll();
    },

    normalizeItem(raw) {
      return {
        id_backoffice: raw.id_backoffice ?? raw.id ?? null,
        sku: Array.isArray(raw.sku) ? raw.sku : raw.sku ? [raw.sku] : [],
        ean: Array.isArray(raw.ean) ? raw.ean : raw.ean ? [raw.ean] : [],
        name: raw.name ?? '',
        stock: Number(raw.stock ?? 0),
        image: raw.image || raw.img || raw.image_url || '',
      };
    },

    sortVal(item, key) {
      return key === 'name'
        ? String(item[key] || '').toLowerCase()
        : Number(item[key] ?? 0);
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = key === 'name' ? 'asc' : 'desc';
      }
    },
    caretClass(key) {
      return {
        up: this.sortKey === key && this.sortDir === 'asc',
        down: this.sortKey === key && this.sortDir === 'desc',
      };
    },
    stockBadge(p) {
      const s = Number(p.stock || 0);
      if (s >= 200) return 'badge-strong';
      if (s >= 100) return 'badge-good';
      if (s >= 50) return 'badge-mid';
      if (s >= 20) return 'badge-low';
      return 'badge-zero';
    },

    // abrir produto ao clicar na linha
    openProduct(id, evt) {
      if (!id) return;
      const path = `/product/${id}`;

      // Ctrl (Windows/Linux) ou Cmd (macOS) -> nova aba
      if (evt && (evt.ctrlKey || evt.metaKey)) {
        const url = this.$router.resolve(path).href; // respeita base/publicPath do router
        window.open(url, '_blank');
        return;
      }

      // click normal -> navegação na mesma aba
      this.$router.push(path);
    },

    exportCsv() {
      const rows = this.sorted.map((p) => ({
        id_backoffice: p.id_backoffice,
        sku: p.sku.join('|'),
        ean: p.ean.join('|'),
        name: p.name,
        stock: p.stock,
      }));
      const headers = Object.keys(
        rows[0] || { id_backoffice: '', sku: '', ean: '', name: '', stock: '' }
      );
      const csv = [
        headers.join(';'),
        ...rows.map((r) =>
          headers.map((h) => String(r[h]).replaceAll(';', ',')).join(';')
        ),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'stock_alto.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
/* Larguras mais generosas */
.container-max {
  max-width: 1600px;
  margin: 0 auto;
}

/* header */
.header {
  border-bottom: 1px solid #eef2f6;
  background: linear-gradient(180deg, #fff 0%, #fbfdff 100%);
}
.hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
}
.mark {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: radial-gradient(100% 100% at 30% 20%, #7db3ff 0%, #3a77b4 100%);
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 800;
  letter-spacing: 0.5px;
  box-shadow: 0 6px 18px rgba(58, 119, 180, 0.25);
}
.title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
}
.subtitle {
  margin: 0;
  color: #6b7a8c;
  font-size: 0.9rem;
}
.spacer {
  flex: 1;
}
.actions {
  display: flex;
  align-items: center;
}

/* stock min filter */
.stock-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}
.stock-filter label {
  font-size: 13px;
  color: #62748a;
  margin: 0;
}
.stock-filter input {
  max-width: 110px;
}
.quick-buttons {
  display: flex;
  gap: 6px;
}

/* info-bar */
.infobar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0 14px;
}
.infochip {
  border: 1px solid #e9eef6;
  background: #fff;
  border-radius: 12px;
  padding: 8px 12px;
  min-width: 180px;
}
.infochip .l {
  color: #6b7a8c;
  font-size: 12px;
}
.infochip .v {
  font-weight: 800;
  font-size: 20px;
}
.muted {
  color: #8191a6;
}

/* card/table */
.card {
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
}
.shadow {
  box-shadow: 0 10px 30px rgba(16, 42, 67, 0.06);
}
.table-wrap {
  overflow-x: auto;
}
.table {
  width: 100%;
  font-size: 13px;
}
.table thead th {
  position: sticky;
  top: 0;
  background: #f7fafc;
  border-top: 0;
  border-bottom: 1px solid #e6edf5;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: #62748a;
  white-space: nowrap;
}
.table tbody tr:hover {
  background: #fbfdff;
}

/* colunas */
.w-id {
  width: 100px;
}
.w-img {
  width: 70px;
}
.w-sku {
  width: 260px;
}
.w-ean {
  width: 300px;
}
.w-stock {
  width: 120px;
}

/* thumb */
.thumb img {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e6edf5;
  background: #f3f6fb;
}

/* nomes longos */
.name .name-text {
  max-width: 680px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* chips (EAN e SKU) */
.ean-chip,
.sku-chip {
  display: inline-block;
  margin: 2px 6px 2px 0;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid #d9e7f5;
  background: #eef5fb;
  color: #2f6aa1;
}
.sku-chips,
.ean {
  white-space: normal;
}

/* sort */
.th-sort {
  cursor: pointer;
  user-select: none;
}
.caret {
  margin-left: 6px;
  border: 5px solid transparent;
  display: inline-block;
}
.caret.up {
  border-bottom-color: #7a8aa0;
}
.caret.down {
  border-top-color: #7a8aa0;
}

/* badges */
.badge {
  display: inline-block;
  min-width: 44px;
  text-align: center;
  padding: 4px 8px;
  border-radius: 8px;
  font-weight: 800;
  font-size: 12px;
}
.badge-strong {
  background: #e8f5e9;
  color: #1b5e20;
  border: 1px solid #c8e6c9;
}
.badge-good {
  background: #edf7ff;
  color: #0d47a1;
  border: 1px solid #d6eaff;
}
.badge-mid {
  background: #fff7e6;
  color: #a15c00;
  border: 1px solid #ffe2b5;
}
.badge-low {
  background: #fff0f0;
  color: #b00020;
  border: 1px solid #ffd1d1;
}
.badge-zero {
  background: #f2f4f7;
  color: #667085;
  border: 1px solid #e3e8ef;
}

/* clique na linha */
.row-click {
  cursor: pointer;
}

/* misc */
.text-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
}

/* loading */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: grid;
  place-items: center;
  z-index: 1050;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e6edf5;
  border-top-color: #3a77b4;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.ml-2 {
  margin-left: 0.5rem;
}
.mr-2 {
  margin-right: 0.5rem;
}
.mr-3 {
  margin-right: 0.75rem;
}
</style>
